import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from "recharts";
import { RefreshCw, Download, ChevronDown, TrendingUp, TrendingDown, Scale } from "lucide-react";

const COLORS = ["#387eff", "#ff4e4e", "#00a859", "#d80097", "#ff9800"];

const BASE_URL = " https://sadaatagrofarms.com";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (val) =>
  Number(val || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

// ─── PDF generator (pure JS, no external lib) ─────────────────────────────────
const generatePDF = (sheet) => {
  const printWindow = window.open("", "_blank");
  const assets = sheet.assets;
  const liabilities = sheet.liabilities;
  const equity = sheet.owner_equity;

  const assetTotal = Number(assets?.total || 0);
  const liabilityTotal = Number(liabilities?.total || 0);
  const equityTotal = Number(equity?.total || 0);
  const finalTotal = Number(sheet.final_total || 0);

  const rowsCA = (assets?.current_assets || [])
    .map(
      (i) => `<tr><td style="padding:8px 12px;">${i.name}</td>
               <td style="padding:8px 12px;text-align:right;">Rs ${fmt(i.price)}</td></tr>`
    )
    .join("");

  const rowsLTA = (assets?.long_term_assets || [])
    .map(
      (i) => `<tr><td style="padding:8px 12px;">${i.name}</td>
               <td style="padding:8px 12px;text-align:right;">Rs ${fmt(i.price)}</td></tr>`
    )
    .join("");

  const rowsCL = (liabilities?.current_liabilities || [])
    .map(
      (i) => `<tr><td style="padding:8px 12px;">${i.name}</td>
               <td style="padding:8px 12px;text-align:right;">Rs ${fmt(i.price)}</td></tr>`
    )
    .join("");

  const rowsLTL = (liabilities?.long_term_liabilities || [])
    .map(
      (i) => `<tr><td style="padding:8px 12px;">${i.name}</td>
               <td style="padding:8px 12px;text-align:right;">Rs ${fmt(i.price)}</td></tr>`
    )
    .join("");

  const rowsOE = (equity?.items || [])
    .map(
      (i) => `<tr><td style="padding:8px 12px;">${i.name}</td>
               <td style="padding:8px 12px;text-align:right;">Rs ${fmt(i.amount)}</td></tr>`
    )
    .join("");

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${sheet.name} – Balance Sheet</title>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Source+Sans+3:wght@400;600&display=swap" rel="stylesheet">
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: 'Source Sans 3', sans-serif;
          background: #f5f7f2;
          color: #1a2e1e;
          padding: 40px;
        }
        .cover {
          background: linear-gradient(135deg, #0f5323 0%, #1a7a35 60%, #2ea84e 100%);
          border-radius: 16px;
          padding: 48px;
          color: white;
          margin-bottom: 36px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .cover-title { font-family: 'Playfair Display', serif; font-size: 38px; line-height: 1.1; }
        .cover-sub { font-size: 14px; opacity: 0.8; margin-top: 8px; }
        .cover-meta { text-align: right; font-size: 13px; opacity: 0.85; }
        .cover-meta strong { display: block; font-size: 22px; font-family: 'Playfair Display', serif; }

        .kpi-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 32px;
        }
        .kpi-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          border-left: 5px solid;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .kpi-label { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #6b8070; }
        .kpi-value { font-size: 28px; font-weight: 700; margin-top: 6px; font-family: 'Playfair Display', serif; }

        .section { margin-bottom: 28px; }
        .section-header {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          color: #0f5323;
          padding: 12px 16px;
          border-bottom: 2px solid #0f5323;
          margin-bottom: 0;
          background: rgba(15,83,35,0.05);
          border-radius: 8px 8px 0 0;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 0 0 10px 10px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        thead tr { background: rgba(15,83,35,0.08); }
        th {
          padding: 10px 12px;
          text-align: left;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #0f5323;
          font-weight: 600;
        }
        td { border-top: 1px solid #e8f0ea; font-size: 14px; color: #2c4030; }
        tr:hover td { background: #f0f7f2; }
        .sub-header td {
          background: rgba(56,126,255,0.06);
          color: #387eff;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: 0.5px;
          padding: 8px 12px;
          border-top: none;
        }
        .total-row td {
          background: rgba(15,83,35,0.08);
          font-weight: 700;
          font-size: 15px;
          padding: 12px;
          border-top: 2px solid #0f5323;
          color: #0f5323;
        }
        .grand-total td {
          background: linear-gradient(135deg, #0f5323, #1a7a35);
          color: white;
          font-weight: 800;
          font-size: 16px;
          padding: 14px 12px;
        }
        .watermark {
          text-align: center;
          margin-top: 48px;
          font-size: 12px;
          color: #99b8a0;
          border-top: 1px solid #d0e4d6;
          padding-top: 20px;
        }
        @media print {
          body { background: white; padding: 20px; }
          .cover { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .kpi-card, table, .kpi-row { break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="cover">
        <div>
          <div class="cover-title">${sheet.name}</div>
          <div class="cover-sub">Official Financial Statement</div>
        </div>
        <div class="cover-meta">
          <strong>Rs ${fmt(finalTotal)}</strong>
          Final Total Balance
          <br><br>
          Generated: ${new Date().toLocaleDateString("en-PK", { year: "numeric", month: "long", day: "numeric" })}
          <br>Sadaat Agro Farms
        </div>
      </div>

      <div class="kpi-row">
        <div class="kpi-card" style="border-color:#00a859;">
          <div class="kpi-label">Total Assets</div>
          <div class="kpi-value" style="color:#00a859;">Rs ${fmt(assetTotal)}</div>
        </div>
        <div class="kpi-card" style="border-color:#ff4e4e;">
          <div class="kpi-label">Total Liabilities</div>
          <div class="kpi-value" style="color:#ff4e4e;">Rs ${fmt(liabilityTotal)}</div>
        </div>
        <div class="kpi-card" style="border-color:#387eff;">
          <div class="kpi-label">Owner's Equity</div>
          <div class="kpi-value" style="color:#387eff;">Rs ${fmt(equityTotal)}</div>
        </div>
      </div>

      <!-- ASSETS -->
      <div class="section">
        <div class="section-header">Assets — ${assets?.name || ""}</div>
        <table>
          <thead><tr><th>Item</th><th style="text-align:right;">Amount (Rs)</th></tr></thead>
          <tbody>
            <tr class="sub-header"><td colspan="2">CURRENT ASSETS</td></tr>
            ${rowsCA}
            <tr class="sub-header"><td colspan="2">LONG-TERM ASSETS</td></tr>
            ${rowsLTA}
            <tr class="total-row"><td>Total Assets</td><td style="text-align:right;">Rs ${fmt(assetTotal)}</td></tr>
          </tbody>
        </table>
      </div>

      <!-- LIABILITIES -->
      <div class="section">
        <div class="section-header">Liabilities — ${liabilities?.name || ""}</div>
        <table>
          <thead><tr><th>Item</th><th style="text-align:right;">Amount (Rs)</th></tr></thead>
          <tbody>
            <tr class="sub-header"><td colspan="2">CURRENT LIABILITIES</td></tr>
            ${rowsCL}
            <tr class="sub-header"><td colspan="2">LONG-TERM LIABILITIES</td></tr>
            ${rowsLTL}
            <tr class="total-row"><td>Total Liabilities</td><td style="text-align:right;">Rs ${fmt(liabilityTotal)}</td></tr>
          </tbody>
        </table>
      </div>

      <!-- OWNER EQUITY -->
      <div class="section">
        <div class="section-header">Owner's Equity — ${equity?.name || ""}</div>
        <table>
          <thead><tr><th>Item</th><th style="text-align:right;">Amount (Rs)</th></tr></thead>
          <tbody>
            ${rowsOE}
            <tr class="total-row"><td>Total Owner's Equity</td><td style="text-align:right;">Rs ${fmt(equityTotal)}</td></tr>
          </tbody>
        </table>
      </div>

      <!-- GRAND TOTAL -->
      <table style="border-radius:10px;overflow:hidden;margin-top:8px;">
        <tbody>
          <tr class="grand-total">
            <td>FINAL TOTAL (Assets + Liabilities + Equity)</td>
            <td style="text-align:right;">Rs ${fmt(finalTotal)}</td>
          </tr>
        </tbody>
      </table>

      <div class="watermark">
        This document was generated automatically by Sadaat Agro Farms Management System &nbsp;•&nbsp; ${new Date().toISOString()}
      </div>

      <script>window.onload = () => { window.print(); }<\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// ─── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: "rgba(15,83,35,0.95)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 10,
        padding: "10px 16px",
        color: "white",
        fontSize: 13,
        backdropFilter: "blur(8px)"
      }}>
        <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.fill || p.color }}>
            Rs {Number(p.value).toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── Main Component ────────────────────────────────────────────────────────────
const BalanceSheet = () => {
  const [sheetList, setSheetList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [sheetDetail, setSheetDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch list of balance sheets
  useEffect(() => {
    setListLoading(true);
    const token = localStorage.getItem("authToken");
    fetch(`${BASE_URL}/api/balancesheet/balance-sheets/`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    })
      .then((r) => r.json())
      .then((data) => {
        setSheetList(Array.isArray(data) ? data : []);
        setListLoading(false);
      })
      .catch((err) => {
        console.error("Balance sheet list error:", err);
        setListLoading(false);
      });
  }, []);

  // Fetch detail when selectedId changes
  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    setSheetDetail(null);
    const token = localStorage.getItem("authToken");
    fetch(`${BASE_URL}/api/balancesheet/balance-sheets/${selectedId}/`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    })
      .then((r) => r.json())
      .then((data) => {
        setSheetDetail(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Balance sheet detail error:", err);
        setLoading(false);
      });
  }, [selectedId]);

  const selectedSheet = sheetList.find((s) => s.id === selectedId);

  // ── derived chart data ──
  const overviewChartData = sheetDetail
    ? [
        { name: "Assets", value: Number(sheetDetail.assets?.total || 0) },
        { name: "Liabilities", value: Number(sheetDetail.liabilities?.total || 0) },
        { name: "Owner Equity", value: Number(sheetDetail.owner_equity?.total || 0) }
      ]
    : [];

  const assetBreakdown = sheetDetail
    ? [
        ...( sheetDetail.assets?.current_assets || []).map((i) => ({
          name: i.name + " (CA)",
          value: Number(i.price),
          type: "Current"
        })),
        ...( sheetDetail.assets?.long_term_assets || []).map((i) => ({
          name: i.name + " (LT)",
          value: Number(i.price),
          type: "Long-term"
        }))
      ]
    : [];

  const liabilityBreakdown = sheetDetail
    ? [
        ...(sheetDetail.liabilities?.current_liabilities || []).map((i) => ({
          name: i.name + " (CL)",
          value: Number(i.price)
        })),
        ...(sheetDetail.liabilities?.long_term_liabilities || []).map((i) => ({
          name: i.name + " (LT)",
          value: Number(i.price)
        }))
      ]
    : [];

  const equityItems = sheetDetail
    ? (sheetDetail.owner_equity?.items || []).map((i) => ({
        name: i.name,
        value: Number(i.amount)
      }))
    : [];

  const radarData = sheetDetail
    ? [
        { subject: "Current Assets", A: (sheetDetail.assets?.current_assets || []).reduce((s, i) => s + Number(i.price), 0) },
        { subject: "Long-term Assets", A: (sheetDetail.assets?.long_term_assets || []).reduce((s, i) => s + Number(i.price), 0) },
        { subject: "Current Liabilities", A: (sheetDetail.liabilities?.current_liabilities || []).reduce((s, i) => s + Number(i.price), 0) },
        { subject: "Long-term Liabilities", A: (sheetDetail.liabilities?.long_term_liabilities || []).reduce((s, i) => s + Number(i.price), 0) },
        { subject: "Owner Equity", A: Number(sheetDetail.owner_equity?.total || 0) }
      ]
    : [];

  return (
    <div className="finance">
      {/* Backdrop when dropdown is open */}
      {dropdownOpen && <div className="budget-dropdown-backdrop" />}

      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1>Balance Sheet</h1>
          <p className="subtitle">Assets · Liabilities · Owner's Equity</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {sheetDetail && (
            <button
              className="refresh-btn"
              style={{ background: "linear-gradient(135deg,#0f5323,#2ea84e)", color: "white", border: "none" }}
              onClick={() => generatePDF(sheetDetail)}
            >
              <Download size={16} />
              Download PDF
            </button>
          )}
          {/* <button className="refresh-btn" onClick={() => selectedId && setSelectedId((id) => { const i = id; setSheetDetail(null); setTimeout(() => setSelectedId(i), 50); return null; }}>
            <RefreshCw size={16} />
            Refresh
          </button> */}
        </div>
      </div>

      {/* ── Balance Sheet Dropdown ── */}
      <div style={{ marginBottom: 28, display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontWeight: 600, color: "#0f5323", fontSize: 14 }}>Select Balance Sheet:</span>
        <div className="budget-custom-dropdown" ref={dropdownRef} style={{ minWidth: 220 }}>
          <div
            className="budget-selected"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <span>
              {listLoading
                ? "Loading..."
                : selectedSheet
                ? `Balance Sheet ${selectedSheet.id} — ${selectedSheet.name}`
                : "Select a Balance Sheet"}
            </span>
            <ChevronDown
              size={16}
              style={{
                transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
                marginLeft: 8,
                flexShrink: 0
              }}
            />
          </div>

          {dropdownOpen && (
            <div className="budget-options">
              <div
                className="budget-option placeholder"
                onClick={() => { setSelectedId(null); setSheetDetail(null); setDropdownOpen(false); }}
              >
                — Select a Balance Sheet —
              </div>
              {sheetList.length === 0 && !listLoading && (
                <div className="budget-option placeholder">No balance sheets found</div>
              )}
              {sheetList.map((sheet) => (
                <div
                  key={sheet.id}
                  className={`budget-option${selectedId === sheet.id ? " selected" : ""}`}
                  onClick={() => { setSelectedId(sheet.id); setDropdownOpen(false); }}
                >
                  Balance Sheet {sheet.id} — {sheet.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#0f5323", fontSize: 16 }}>
          <RefreshCw size={28} style={{ animation: "spin 1s linear infinite", marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
          Loading Balance Sheet…
        </div>
      )}

      {/* ── No selection ── */}
      {!loading && !sheetDetail && !selectedId && (
        <div style={{
          textAlign: "center",
          padding: "80px 40px",
          background: "rgba(15,83,35,0.03)",
          borderRadius: 16,
          border: "2px dashed rgba(15,83,35,0.15)"
        }}>
          <Scale size={48} style={{ color: "#c8ddc0", marginBottom: 16 }} />
          <p style={{ color: "#6b8070", fontSize: 16 }}>Select a balance sheet from the dropdown above to view its data</p>
        </div>
      )}

      {/* ── Detail View ── */}
      {!loading && sheetDetail && (() => {
        const assetTotal = Number(sheetDetail.assets?.total || 0);
        const liabilityTotal = Number(sheetDetail.liabilities?.total || 0);
        const equityTotal = Number(sheetDetail.owner_equity?.total || 0);
        const finalTotal = Number(sheetDetail.final_total || 0);

        return (
          <>
            {/* KPI Cards */}
            <div className="stats-grid" style={{ marginBottom: 24 }}>
              <div className="stat-card" style={{ borderLeft: "4px solid #00a859" }}>
                <TrendingUp size={26} style={{ color: "#00a859" }} />
                <h3 style={{ color: "#00a859" }}>Rs {fmt(assetTotal)}</h3>
                <p>Total Assets</p>
              </div>
              <div className="stat-card" style={{ borderLeft: "4px solid #ff4e4e" }}>
                <TrendingDown size={26} style={{ color: "#ff4e4e" }} />
                <h3 style={{ color: "#ff4e4e" }}>Rs {fmt(liabilityTotal)}</h3>
                <p>Total Liabilities</p>
              </div>
              <div className="stat-card" style={{ borderLeft: "4px solid #387eff" }}>
                <Scale size={26} style={{ color: "#387eff" }} />
                <h3 style={{ color: "#387eff" }}>Rs {fmt(equityTotal)}</h3>
                <p>Owner's Equity</p>
              </div>
              <div className="stat-card" style={{ borderLeft: "4px solid #ff9800", background: "linear-gradient(135deg,rgba(255,152,0,0.07),rgba(255,152,0,0.02))" }}>
                <h3 style={{ color: "#ff9800", fontSize: 18 }}>Rs {fmt(finalTotal)}</h3>
                <p>Final Total</p>
              </div>
            </div>

            {/* ── Overview Bar Chart ── */}
            <h2 className="section-title">Overview — Bar Chart</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={overviewChartData} barCategoryGap="35%">
                  <XAxis dataKey="name" tick={{ fontSize: 13, fill: "#3c6643" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#3c6643" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {overviewChartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* ── Overview Pie Chart ── */}
            <h2 className="section-title">Overview — Pie Chart</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={overviewChartData} dataKey="value" nameKey="name" outerRadius={110} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {overviewChartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* ── Asset Breakdown Chart ── */}
            {assetBreakdown.length > 0 && (
              <>
                <h2 className="section-title">Assets Breakdown</h2>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={assetBreakdown}>
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#3c6643" }} />
                      <YAxis tick={{ fontSize: 12, fill: "#3c6643" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {assetBreakdown.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}

            {/* ── Radar Chart ── */}
            <h2 className="section-title">Financial Structure — Radar</h2>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="rgba(15,83,35,0.2)" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fill: "#3c6643" }} />
                  <PolarRadiusAxis tick={{ fontSize: 10, fill: "#3c6643" }} />
                  <Radar dataKey="A" stroke="#387eff" fill="#387eff" fillOpacity={0.3} />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* ══════════════════════════════
                TABLES
            ══════════════════════════════ */}

            {/* ── Assets Table ── */}
            <h2 className="section-title">Assets — {sheetDetail.assets?.name}</h2>
            <div className="chart-container">
              <div className="finance-table-wrapper">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Item Name</th>
                      <th>Amount (Rs)</th>
                      <th>% of Total Assets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Current Assets */}
                    <tr style={{ background: "rgba(56,126,255,0.06)" }}>
                      <td colSpan={5} style={{ fontWeight: 700, color: "#387eff", padding: "8px 14px", fontSize: 12, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                        Current Assets
                      </td>
                    </tr>
                    {(sheetDetail.assets?.current_assets || []).map((item, i) => {
                      const pct = assetTotal > 0 ? ((Number(item.price) / assetTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={`ca-${item.id}`}>
                          <td style={{ padding: "10px 14px", color: "#6b8070" }}>{i + 1}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ background: "rgba(0,168,89,0.1)", color: "#00a859", borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 600 }}>Current</span>
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 500 }}>{item.name}</td>
                          <td className="amount-cell positive" style={{ padding: "10px 14px" }}>Rs {fmt(item.price)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 7, background: "rgba(15,83,35,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 90 }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: "#00a859", borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Long-term Assets */}
                    <tr style={{ background: "rgba(56,126,255,0.06)" }}>
                      <td colSpan={5} style={{ fontWeight: 700, color: "#387eff", padding: "8px 14px", fontSize: 12, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                        Long-Term Assets
                      </td>
                    </tr>
                    {(sheetDetail.assets?.long_term_assets || []).map((item, i) => {
                      const pct = assetTotal > 0 ? ((Number(item.price) / assetTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={`lta-${item.id}`}>
                          <td style={{ padding: "10px 14px", color: "#6b8070" }}>{i + 1}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ background: "rgba(56,126,255,0.1)", color: "#387eff", borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 600 }}>Long-term</span>
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 500 }}>{item.name}</td>
                          <td className="amount-cell positive" style={{ padding: "10px 14px" }}>Rs {fmt(item.price)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 7, background: "rgba(15,83,35,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 90 }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: "#387eff", borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="total-row">
                      <td colSpan={3}><strong>Total Assets</strong></td>
                      <td colSpan={2} className="amount-cell positive"><strong>Rs {fmt(assetTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Liabilities Table ── */}
            <h2 className="section-title">Liabilities — {sheetDetail.liabilities?.name}</h2>
            <div className="chart-container">
              <div className="finance-table-wrapper">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Category</th>
                      <th>Item Name</th>
                      <th>Amount (Rs)</th>
                      <th>% of Total Liabilities</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: "rgba(255,78,78,0.06)" }}>
                      <td colSpan={5} style={{ fontWeight: 700, color: "#000000", padding: "8px 14px", fontSize: 12, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                        Current Liabilities
                      </td>
                    </tr>
                    {(sheetDetail.liabilities?.current_liabilities || []).map((item, i) => {
                      const pct = liabilityTotal > 0 ? ((Number(item.price) / liabilityTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={`cl-${item.id}`}>
                          <td style={{ padding: "10px 14px", color: "#6b8070" }}>{i + 1}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ background: "rgba(255,78,78,0.1)", color: "#ff4e4e", borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 600 }}>Current</span>
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 500 }}>{item.name}</td>
                          <td className="amount-cell negative" style={{ padding: "10px 14px" }}>Rs {fmt(item.price)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 7, background: "rgba(255,78,78,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 90 }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: "#ff4e4e", borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    <tr style={{ background: "rgba(255,78,78,0.06)" }}>
                      <td colSpan={5} style={{ fontWeight: 700, color: "#000000", padding: "8px 14px", fontSize: 12, letterSpacing: "0.5px", textTransform: "uppercase" }}>
                        Long-Term Liabilities
                      </td>
                    </tr>
                    {(sheetDetail.liabilities?.long_term_liabilities || []).map((item, i) => {
                      const pct = liabilityTotal > 0 ? ((Number(item.price) / liabilityTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={`ltl-${item.id}`}>
                          <td style={{ padding: "10px 14px", color: "#6b8070" }}>{i + 1}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ background: "rgba(216,0,151,0.1)", color: "#d80097", borderRadius: 6, padding: "3px 8px", fontSize: 12, fontWeight: 600 }}>Long-term</span>
                          </td>
                          <td style={{ padding: "10px 14px", fontWeight: 500 }}>{item.name}</td>
                          <td className="amount-cell negative" style={{ padding: "10px 14px" }}>Rs {fmt(item.price)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 7, background: "rgba(216,0,151,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 90 }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: "#d80097", borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="total-row">
                      <td colSpan={3}><strong>Total Liabilities</strong></td>
                      <td colSpan={2} className="amount-cell negative"><strong>Rs {fmt(liabilityTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Owner Equity Table ── */}
            <h2 className="section-title">Owner's Equity — {sheetDetail.owner_equity?.name}</h2>
            <div className="chart-container">
              <div className="finance-table-wrapper">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item Name</th>
                      <th>Amount (Rs)</th>
                      <th>% of Total Equity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sheetDetail.owner_equity?.items || []).map((item, i) => {
                      const pct = equityTotal > 0 ? ((Number(item.amount) / equityTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={`oe-${item.id}`}>
                          <td style={{ padding: "10px 14px", color: "#6b8070" }}>{i + 1}</td>
                          <td style={{ padding: "10px 14px", fontWeight: 500 }}>{item.name}</td>
                          <td className="amount-cell positive" style={{ padding: "10px 14px" }}>Rs {fmt(item.amount)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 7, background: "rgba(56,126,255,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 90 }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: "#387eff", borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="total-row">
                      <td colSpan={2}><strong>Total Owner's Equity</strong></td>
                      <td colSpan={2} className="amount-cell positive"><strong>Rs {fmt(equityTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Final Summary Table ── */}
            <h2 className="section-title">Final Summary</h2>
            <div className="chart-container">
              <div className="finance-table-wrapper">
                <table className="finance-table">
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>Sub-section</th>
                      <th>Items</th>
                      <th>Total (Rs)</th>
                      <th>% of Final Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        section: "Assets",
                        sub: "Current Assets",
                        items: sheetDetail.assets?.current_assets?.length || 0,
                        total: (sheetDetail.assets?.current_assets || []).reduce((s, i) => s + Number(i.price), 0),
                        color: "#00a859"
                      },
                      {
                        section: "Assets",
                        sub: "Long-term Assets",
                        items: sheetDetail.assets?.long_term_assets?.length || 0,
                        total: (sheetDetail.assets?.long_term_assets || []).reduce((s, i) => s + Number(i.price), 0),
                        color: "#387eff"
                      },
                      {
                        section: "Liabilities",
                        sub: "Current Liabilities",
                        items: sheetDetail.liabilities?.current_liabilities?.length || 0,
                        total: (sheetDetail.liabilities?.current_liabilities || []).reduce((s, i) => s + Number(i.price), 0),
                        color: "#ff4e4e"
                      },
                      {
                        section: "Liabilities",
                        sub: "Long-term Liabilities",
                        items: sheetDetail.liabilities?.long_term_liabilities?.length || 0,
                        total: (sheetDetail.liabilities?.long_term_liabilities || []).reduce((s, i) => s + Number(i.price), 0),
                        color: "#d80097"
                      },
                      {
                        section: "Owner's Equity",
                        sub: "Equity Items",
                        items: sheetDetail.owner_equity?.items?.length || 0,
                        total: equityTotal,
                        color: "#ff9800"
                      }
                    ].map((row, idx) => {
                      const pct = finalTotal > 0 ? ((row.total / finalTotal) * 100).toFixed(1) : 0;
                      return (
                        <tr key={idx}>
                          <td style={{ padding: "10px 14px", fontWeight: 600, color: row.color }}>{row.section}</td>
                          <td style={{ padding: "10px 14px" }}>{row.sub}</td>
                          <td style={{ padding: "10px 14px", color: "#6b8070" }}>{row.items} items</td>
                          <td className="amount-cell positive" style={{ padding: "10px 14px" }}>Rs {fmt(row.total)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1, height: 7, background: "rgba(15,83,35,0.1)", borderRadius: 4, overflow: "hidden", maxWidth: 100 }}>
                                <div style={{ width: `${pct}%`, height: "100%", background: row.color, borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{
                      background: "linear-gradient(135deg,#0f5323,#1a7a35)",
                    }}>
                      <td colSpan={3} style={{ color: "white", fontWeight: 800, padding: "14px", fontSize: 15 }}>FINAL TOTAL</td>
                      <td colSpan={2} style={{ color: "white", fontWeight: 800, padding: "14px", fontSize: 15 }}>Rs {fmt(finalTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        );
      })()}

      {/* Spin keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default BalanceSheet;